/**
 * "Muted in this channel" (2026-10-28, owner request): a voice channel's
 * settings list every channel mute and deafen there, with who did it and
 * when, and a Lift button for each. It is how a moderator lifts one while
 * the person is somewhere else (or out of voice): the member menus offer
 * channel mute only for the channel the person is in now, and the owner's
 * "there is no unmute button" must never come back.
 *
 * Shown to anyone with Mute Members or Deafen Members in that channel (the
 * server's rule for reading the list); each Lift needs that flag's own
 * permission there, as the server checks, and nothing else: no role
 * hierarchy (Discord's Mute / Deafen Members), and a mute on the owner can
 * be lifted too. Where a Lift is missing, the row says why (QA 2026-10-28
 * round 1: it was dropped without a word). Live:
 * NX_CHANNEL_VOICE_MUTE_UPDATE, which the server sends only to that
 * channel's moderators.
 */
import {
  Permissions,
  type Channel,
  type ChannelVoiceMute,
  type Snowflake,
} from '@neosocial/protocol';
import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { setChannelFlag, type ServerFlag } from '../mod/memberMenu';
import { useData } from '../store/data';
import { can, channelPermissions, userName } from '../store/selectors';
import { onGatewayEvent } from '../store/umEvents';
import { Avatar, Btn, Icon } from '../ui/kit';

/** Mute Members or Deafen Members in this channel: who sees its list. */
export function canSeeVoiceMutes(
  d: Parameters<typeof channelPermissions>[0],
  channel: Channel,
): boolean {
  const p = channelPermissions(d, channel);
  return can(p, Permissions.MUTE_MEMBERS) || can(p, Permissions.DEAFEN_MEMBERS);
}

type Load = { st: 'loading' } | { st: 'error' } | { st: 'ok'; rows: ChannelVoiceMute[] };

/** The newest change first, as the server orders the list. */
function newest(r: ChannelVoiceMute): number {
  return Math.max(
    r.mute && r.mute_at ? Date.parse(r.mute_at) : 0,
    r.deaf && r.deaf_at ? Date.parse(r.deaf_at) : 0,
  );
}

/**
 * The list after one NX_CHANNEL_VOICE_MUTE_UPDATE: the event carries that
 * person's whole state here, so it replaces their row (or drops it when
 * both flags are off), whatever came before.
 */
export function applyVoiceMuteEvent(
  rows: ChannelVoiceMute[],
  d: ChannelVoiceMute,
): ChannelVoiceMute[] {
  const prev = rows.find((r) => r.user_id === d.user_id);
  const rest = rows.filter((r) => r.user_id !== d.user_id);
  if (!d.mute && !d.deaf) return rest;
  const next: ChannelVoiceMute = { ...d, ...(prev?.user && !d.user ? { user: prev.user } : {}) };
  return [next, ...rest].sort((a, b) => newest(b) - newest(a));
}

function Row({
  row,
  channel,
  canMute,
  canDeaf,
  onLifted,
}: {
  row: ChannelVoiceMute;
  channel: Channel;
  canMute: boolean;
  canDeaf: boolean;
  onLifted(userId: Snowflake, flag: ServerFlag): void;
}) {
  const { t, fmt } = useT();
  const gid = channel.guild_id ?? null;
  const user = useData((d) => d.users[row.user_id] ?? row.user);
  const name = useData((d) =>
    d.users[row.user_id]
      ? userName(d, row.user_id, gid)
      : row.user?.global_name || row.user?.username || row.user_id,
  );
  // who did each (one string, so the selector's answer is stable)
  const [muteBy, deafBy] = useData((d) =>
    [row.mute_by, row.deaf_by]
      .map((id) => (id && d.users[id] ? userName(d, id, gid) : ''))
      .join('\n'),
  ).split('\n');
  const by = (flag: ServerFlag) => (flag === 'mute' ? muteBy : deafBy) || t('um.audit.someone');
  // where they are now, when it is not here: the list holds while they are away
  const elsewhere = useData((d) => {
    const vs = gid ? d.voiceStates[gid]?.[row.user_id] : undefined;
    const at = vs?.channel_id;
    return at && at !== channel.id ? (d.channels[at]?.name ?? null) : null;
  });
  const [busy, setBusy] = useState<ServerFlag | null>(null);
  const lift = async (flag: ServerFlag) => {
    setBusy(flag);
    const ok = await setChannelFlag(channel.id, row.user_id, flag, false);
    setBusy(null);
    if (ok) onLifted(row.user_id, flag);
  };
  const line = (flag: ServerFlag) => {
    const when = flag === 'mute' ? row.mute_at : row.deaf_at;
    // that flag's own permission here, as the server checks (no hierarchy)
    const allowed = flag === 'mute' ? canMute : canDeaf;
    return (
      <div className="ns-chmute-f" data-flag={flag === 'mute' ? 'channel-mute' : 'channel-deaf'}>
        <span className="nx-vflag red" aria-hidden="true">
          <Icon name={flag === 'mute' ? 'mic-off-ch' : 'headphones-off-ch'} />
        </span>
        <span className="ns-chmute-what">
          <b>{t(flag === 'mute' ? 'um.chmute.muted' : 'um.chmute.deafened')}</b>
          <small>
            {t('um.chmute.by', { actor: by(flag), time: when ? fmt.stamp(when) : '—' })}
          </small>
        </span>
        {allowed ? (
          <Btn
            size="sm"
            kind="ghost"
            icon={flag === 'mute' ? 'mic' : 'headphones'}
            busy={busy === flag}
            disabled={!!busy}
            data-lift={flag}
            onClick={() => void lift(flag)}
          >
            {t(flag === 'mute' ? 'um.chmute.liftMute' : 'um.chmute.liftDeaf')}
          </Btn>
        ) : (
          <small className="ns-chmute-why" data-no-lift={flag}>
            {t(flag === 'mute' ? 'um.chmute.needMute' : 'um.chmute.needDeaf')}
          </small>
        )}
      </div>
    );
  };
  return (
    <li className="ns-chmute-row" data-user={row.user_id}>
      <span className="ns-chmute-who">
        <Avatar user={user} name={name} size={28} />
        <span className="ns-chmute-name">
          <b dir="auto">{name}</b>
          {elsewhere ? (
            <small dir="auto">{t('um.chmute.where', { channel: elsewhere })}</small>
          ) : null}
        </span>
      </span>
      {row.mute ? line('mute') : null}
      {row.deaf ? line('deaf') : null}
    </li>
  );
}

/** The "Muted in this channel" section of a voice channel's settings. */
export function ChannelVoiceMutes({ channel }: { channel: Channel }) {
  const { t } = useT();
  const canMute = useData((d) => can(channelPermissions(d, channel), Permissions.MUTE_MEMBERS));
  const canDeaf = useData((d) => can(channelPermissions(d, channel), Permissions.DEAFEN_MEMBERS));
  const [load, setLoad] = useState<Load>({ st: 'loading' });
  useEffect(() => {
    let live = true;
    // Events that arrive while the first answer is on its way: that answer
    // may predate them, so they are applied on top of it, in order (QA
    // 2026-10-28, channel mute round 2: they were dropped, and a mute made
    // while the list opened had no Lift until the dialog was reopened).
    let early: ChannelVoiceMute[] | null = [];
    api.um
      .channelVoiceMutes(channel.id)
      .then((rows) => {
        if (!live) return;
        const queued = early ?? [];
        early = null;
        setLoad({ st: 'ok', rows: queued.reduce(applyVoiceMuteEvent, rows) });
      })
      .catch(() => {
        if (!live) return;
        early = null;
        setLoad({ st: 'error' });
      });
    // live: applied and lifted anywhere (another moderator, a member menu)
    const off = onGatewayEvent((f) => {
      if (f.t !== 'NX_CHANNEL_VOICE_MUTE_UPDATE' || f.d.channel_id !== channel.id) return;
      const { guild_id: _g, ...d } = f.d;
      if (early) {
        early.push(d);
        return;
      }
      setLoad((l) => (l.st === 'ok' ? { st: 'ok', rows: applyVoiceMuteEvent(l.rows, d) } : l));
    });
    return () => {
      live = false;
      off();
    };
  }, [channel.id]);
  const onLifted = (userId: Snowflake, flag: ServerFlag) =>
    setLoad((l) => {
      if (l.st !== 'ok') return l;
      const rows = l.rows
        .map((r) =>
          r.user_id !== userId
            ? r
            : flag === 'mute'
              ? { ...r, mute: false, mute_by: null, mute_at: null }
              : { ...r, deaf: false, deaf_by: null, deaf_at: null },
        )
        .filter((r) => r.mute || r.deaf);
      return { st: 'ok', rows };
    });
  return (
    <section className="ns-chmute" aria-labelledby={`chmute-${channel.id}`} data-voice-mutes="">
      <h4 id={`chmute-${channel.id}`}>
        <Icon name="mic-off-ch" />
        {t('um.chmute.title')}
      </h4>
      <p className="ns-help">{t('um.chmute.help')}</p>
      {load.st === 'loading' ? (
        <p className="ns-muted">
          <i className="nx-spin" /> {t('ns.loading')}
        </p>
      ) : load.st === 'error' ? (
        <p className="ns-note warn">{t('um.chmute.failed')}</p>
      ) : load.rows.length === 0 ? (
        <p className="ns-muted" data-empty="">
          {t('um.chmute.empty')}
        </p>
      ) : (
        <ul className="ns-chmute-list">
          {load.rows.map((r) => (
            <Row
              key={r.user_id}
              row={r}
              channel={channel}
              canMute={canMute}
              canDeaf={canDeaf}
              onLifted={onLifted}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
